import { formTemplatePickerOpenAtom, pendingFormTemplateIdAtom } from '@/atoms/chatAtoms';
import { newlyCreatedTemplateAtom } from '@/atoms/formFillingAtom';
import type { SendMessageParams } from '@/components/chat/types/types';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

interface UseFormTemplateFillParams {
  sendMessage: (params: SendMessageParams) => Promise<void>;
  setSuggestionPreview: (value: string | null) => void;
  setPopulateValue: (value: string | null) => void;
  /** When true, selecting a template must not start a completion. */
  sendBlocked?: boolean;
}

export const useFormTemplateFill = ({
  sendMessage,
  setSuggestionPreview,
  setPopulateValue,
  sendBlocked = false,
}: UseFormTemplateFillParams) => {
  const { t } = useTranslations();
  const [isFormTemplatePickerOpen, setIsFormTemplatePickerOpen] = useAtom(
    formTemplatePickerOpenAtom
  );
  const setPendingFormTemplateId = useSetAtom(pendingFormTemplateIdAtom);
  const [newlyCreatedTemplate, setNewlyCreatedTemplate] = useAtom(newlyCreatedTemplateAtom);

  const handleFillVerbClick = useCallback(() => {
    setSuggestionPreview(null);
    setIsFormTemplatePickerOpen(true);
  }, [setSuggestionPreview, setIsFormTemplatePickerOpen]);

  const handleLinkSessionForFill = useCallback(() => {
    setPopulateValue('#');
  }, [setPopulateValue]);

  const handleFormTemplateSelected = useCallback(
    (templateId: string, templateName: string) => {
      if (sendBlocked) return;
      setPendingFormTemplateId(templateId);
      void sendMessage({
        content: t('chat.formTemplatePicker.fillFormMessage', { templateName }),
      });
    },
    [sendMessage, setPendingFormTemplateId, t, sendBlocked]
  );

  useEffect(() => {
    if (newlyCreatedTemplate) {
      handleFormTemplateSelected(newlyCreatedTemplate.id, newlyCreatedTemplate.name);
      setNewlyCreatedTemplate(null);
    }
  }, [newlyCreatedTemplate, setNewlyCreatedTemplate, handleFormTemplateSelected]);

  const closeFormTemplatePicker = useCallback(() => {
    setIsFormTemplatePickerOpen(false);
  }, [setIsFormTemplatePickerOpen]);

  return {
    isFormTemplatePickerOpen,
    closeFormTemplatePicker,
    handleFillVerbClick,
    handleLinkSessionForFill,
    handleFormTemplateSelected,
  };
};
